import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { PublicShell } from "@/components/public/public-shell";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { CheckCircle2, Loader2 } from "lucide-react";
import { getFormForFill, submitForm } from "@/lib/forms.functions";
import { saveProfileSection } from "@/lib/clinic-profile.functions";
import { FormRenderer } from "@/components/form-renderer";
import { clinicAccessQuery } from "@/lib/clinic-access";

const q = (slug: string) => queryOptions({ queryKey: ["form", slug], queryFn: () => getFormForFill({ data: { slug } }) });

const PROFILE_SECTION_SLUGS = [
  "profile-development",
  "profile-pillars",
  "profile-family",
  "profile-routine",
  "profile-school",
  "profile-parents",
] as const;
const search = z.object({
  case: z.string().uuid().optional(),
  profileId: z.string().uuid().optional(),
  section: z.enum(PROFILE_SECTION_SLUGS).optional(),
});

export const Route = createFileRoute("/_authenticated/forms/$slug")({
  validateSearch: (s) => search.parse(s),
  beforeLoad: async ({ context }) => {
    const access = await context.queryClient.ensureQueryData(clinicAccessQuery());
    if (!access?.hasClinic) throw redirect({ to: "/portal", replace: true });
  },
  loader: ({ context, params }) => context.queryClient.ensureQueryData(q(params.slug)),
  head: () => ({ meta: [{ title: "نموذج" }, { name: "robots", content: "noindex" }] }),
  errorComponent: ({ error }) => <p className="p-10 text-brown-500">{String(error)}</p>,
  notFoundComponent: () => <p className="p-10">النموذج غير متاح.</p>,
  component: PageInShell,
});

function Page() {
  const slug = Route.useParams().slug;
  const searchP = Route.useSearch();
  const { data: form } = useSuspenseQuery(q(slug));
  const submit = useServerFn(submitForm);
  const saveSection = useServerFn(saveProfileSection);
  const navigate = useNavigate();

  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!form) {
    return <p className="p-10 text-brown-500">هذا النموذج غير متاح حاليًا.</p>;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await submit({
        data: {
          slug,
          answers,
          linked_entity: searchP.case ? "case" : undefined,
          linked_entity_id: searchP.case,
        },
      });
      // Phase 2 — if this is a profile section fill, mirror the answers into
      // the case_profiles JSONB domain and recompute completeness.
      if (
        searchP.profileId &&
        searchP.section &&
        (form as any)?.category === "profile" &&
        searchP.section === slug
      ) {
        try {
          await saveSection({
            data: {
              profileId: searchP.profileId,
              sectionSlug: searchP.section,
              formSubmissionId: res.id,
            },
          });
        } catch (err: any) {
          toast.error(err?.message ?? "تم إرسال النموذج لكن تعذّر تحديث ملف الطفل.");
        }
      }
      setDone(true);
    } catch (err: any) { toast.error(err?.message ?? "فشل الإرسال"); } finally { setBusy(false); }
  }

  if (done) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-16">
        <div className="rounded-ds-xl bg-surface p-10 text-center shadow-sm">
          <CheckCircle2 className="mx-auto h-14 w-14 text-primary" />
          <h1 className="mt-4 font-display text-2xl font-extrabold text-foreground">تم استلام إجابتك</h1>
          <p className="mt-2 text-brown-500">
            {searchP.profileId ? "تم تحديث ملف طفلك. بقية الأقسام بنفس اللوحة." : "شكرًا لك. سنتواصل معك في أقرب فرصة."}
          </p>
          <div className="mt-6 flex justify-center gap-2">
            <Link
              to="/portal"
              search={searchP.profileId ? ({ tab: "child_file" } as any) : ({} as any)}
              className="rounded-full bg-primary px-5 py-2 font-display text-sm font-bold text-primary-foreground shadow-sm hover:bg-primary-hover"
            >
              {searchP.profileId ? "العودة إلى ملف الطفل" : "العودة إلى لوحتي"}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mx-auto max-w-2xl px-5 py-10">
      <p className="kicker text-amber-800">نموذج</p>
      <h1 className="mt-2 font-display text-3xl font-extrabold text-foreground">{form.title_ar}</h1>
      {form.description_ar && <p className="mt-3 leading-loose text-brown-500">{form.description_ar}</p>}

      <div className="mt-8 rounded-ds-xl bg-surface p-6 shadow-sm md:p-8">
        <FormRenderer schema={(form as any).schema} answers={answers} onChange={(id, v) => setAnswers((a) => ({ ...a, [id]: v }))} />
      </div>

      <div className="mt-6 flex justify-end">
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-display text-sm font-bold text-primary-foreground shadow-sm hover:bg-primary-hover disabled:opacity-50"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} إرسال الإجابة
        </button>
      </div>
    </form>
  );
}

function PageInShell() {
  return (
    <PublicShell ground="bg-cream-200">
      <Page />
    </PublicShell>
  );
}
